import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { watchName } from '../lib/garminDevices'

// The account's watch, from the PWA's side. device_tokens is service-role-only;
// the two RPCs in 022_member_seats.sql expose exactly this much of our own row:
// when it was paired (no row = no watch), which model it is (029 — null until
// the watch's first sync, or from a watch build too old to say) and a way to
// drop it. One row per account
// is what "one watch per person" is — pairing again replaces it, which the
// Profile copy says before handing out a code.
export function useWatch(userId: string | null) {
  const qc = useQueryClient()
  const key = ['watch-pairing', userId]
  const [code, setCode] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Keyed by user, so signing out (or in as someone else) never shows the
  // previous account's watch while the fresh answer is on its way.
  const pairing = useQuery({
    queryKey: key,
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('watch_pairing')
      if (error) throw error
      const row = (data as { created_at: string; device_part: string | null }[] | null)?.[0]
      return row ? { pairedAt: row.created_at, model: watchName(row.device_part) } : null
    },
  })
  const watch = userId ? pairing.data ?? null : null
  const pairedAt = watch?.pairedAt ?? null
  const model = watch?.model ?? null

  // The code is single-use with a five-minute expiry (012); the watch turns it
  // into the token, so the pairing shows up on the next refresh, not here.
  async function generateCode() {
    setBusy(true)
    const { data, error } = await supabase.rpc('generate_watch_pairing_code')
    if (!error && data) setCode(data as string)
    setBusy(false)
  }

  // Once entered on the watch, the code is spent — re-read the pairing so the
  // section flips from the code to "Connected" without a reload.
  function dismissCode() {
    setCode(null)
    qc.invalidateQueries({ queryKey: key })
  }

  async function unpair() {
    setBusy(true)
    const { error } = await supabase.rpc('unpair_watch')
    if (!error) qc.setQueryData(key, null)
    setBusy(false)
  }

  return { pairedAt, model, code, busy, generateCode, dismissCode, unpair }
}
